import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { BLOG_DOC_NAME, db } from '../../../../app/firebase/firebaseConfig'; // Adjust this import path to your Firebase config
import { Blog } from '../../../../app/utlis/type';
import { fetchBlogs } from '../../../../app/helper/functions';

// Define the Blog interface based on your Firestore document structure


const BlogList: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);

  useEffect(() => {
    try {
      setLoading(true);
      fetchBlogs().then((res) => setBlogs(res))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred fetching blogs');
      setLoading(false);
    }
    finally {
      setLoading(false);
    }
  }, []);



  const handleDeleteBlog = async (blogId: string) => {
    try {
      setDeleting(blogId);

      // Reference to the blog document
      const blogRef = doc(db, BLOG_DOC_NAME, blogId);

      // Delete the document
      await deleteDoc(blogRef);

      // Update the state to remove the deleted blog
      setBlogs(prev => prev.filter(blog => blog.id !== blogId));

      // Hide confirmation dialog
      setShowConfirmation(null);
    } catch (err) {
      console.error('Error deleting blog:', err);
      setError(err instanceof Error ? err.message : 'An error occurred deleting the blog');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  if (blogs.length === 0) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-300">No blogs found</h3>
        <p className="mt-2 text-gray-400">Create your first blog post to get started.</p>
        <Link
          to="/create-blog"
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Create Blog Post
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">My Blog Posts</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {blogs.map((blog) => (
          <div key={blog.id} className="bg-blackDarkTheme rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 relative">
            <div className="h-48 overflow-hidden">
              <img
                src={blog.imageUrl}
                alt={blog.title}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              />
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-2 mb-2">
                {blog.categories && blog.categories.slice(0, 3).map((category, index) => (
                  <span key={index} className="px-2 py-1 text-xs font-medium bg-yellow text-black rounded-full">
                    {category}
                  </span>
                ))}
                {blog.categories && blog.categories.length > 3 && (
                  <span className="px-2 py-1 text-xs font-medium bg-gray-600 text-white rounded-full">
                    +{blog.categories.length - 3} more
                  </span>
                )}
              </div>
              <h3 className="text-xl font-semibold text-white mb-2 line-clamp-1">{blog.title}</h3>
              <p className="text-gray-300 text-sm mb-4 line-clamp-3" dangerouslySetInnerHTML={{__html: (blog?.description) || ''}}></p>
              <div className="flex flex-wrap mb-2">
                {blog.hashtags && blog.hashtags.slice(0, 3).map((hashtag, index) => (
                  <span key={index} className="px-1 py-1 text-xs font-medium text-white rounded-full">
                    #{hashtag}
                  </span>
                ))}
                {blog.hashtags && blog.hashtags.length > 3 && (
                  <span className="px-2 py-1 text-xs font-medium bg-gray-600 text-white rounded-full">
                    +{blog.hashtags.length - 3}
                  </span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <div className="text-gray-400 text-xs">
                  {typeof blog.createdAt === 'object' && blog.createdAt?.toDate
                    ? blog.createdAt.toDate().toLocaleDateString()
                    : typeof blog.createdAt === 'string'
                      ? new Date(blog.createdAt).toLocaleDateString()
                      : 'Unknown date'}
                </div>
                <div className="flex items-center space-x-2">

                  <Link
                    to={`/edit-blog/${blog.id}`}
                    className="text-yellow-500 hover:text-yellow-400 p-1"
                    aria-label="Edit blog"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 0L19.5 7.586m-2.828-2.828L3.586 17.172a2 2 0 00-.586 1.414v1.828a2 2 0 002 2h1.828a2 2 0 001.414-.586L21.328 8.742" />
                    </svg>
                  </Link>
                  <button
                    onClick={() => setShowConfirmation(blog.id)}
                    className="text-red-500 hover:text-red-400 p-1"
                    aria-label="Delete blog"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Confirmation Dialog */}
            {showConfirmation === blog.id && (
              <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4">
                <div className="bg-gray-700 p-4 rounded-lg max-w-xs w-full text-center">
                  <p className="text-white mb-4">Are you sure you want to delete this blog post?</p>
                  <div className="flex justify-center space-x-3">
                    <button
                      onClick={() => setShowConfirmation(null)}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                      disabled={deleting === blog.id}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteBlog(blog.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
                      disabled={deleting === blog.id}
                    >
                      {deleting === blog.id ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Deleting
                        </>
                      ) : (
                        'Delete'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlogList; 